import { useEffect, useRef } from 'react'

import type { ComicPanelAspectPreset } from '../../../lib/comicProjectSettings'
import type { ComicReaderPageEntry } from '../../../lib/comicReaderModel'
import type { ComicEpisode } from '../../../lib/comicTypes'
import { ComicReaderPageContent } from './ComicReaderPageContent'

type Props = {
  angular: boolean
  entries: ComicReaderPageEntry[]
  episodesById: Map<string, ComicEpisode>
  panelAspect: ComicPanelAspectPreset
  scrollToKey?: string | null
  onNavigatePage?: (episodeId: string, pageId: string, panelId?: string) => void
}

/**
 * 滚动阅读：整页图上下无缝衔接（条漫式），话次切换仅细分割线、不插标题块。
 */
export function ComicReaderScrollView({
  angular,
  entries,
  episodesById,
  panelAspect,
  scrollToKey,
  onNavigatePage,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map())

  useEffect(() => {
    if (!scrollToKey) return
    const el = itemRefs.current.get(scrollToKey)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollToKey])

  let lastEpisodeId: string | null = null

  return (
    <div
      ref={scrollRef}
      className="scroll-region h-full min-h-0 flex-1 overflow-y-auto overscroll-y-contain bg-[color-mix(in_srgb,var(--surface)_88%,#000_12%)]"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col">
        {entries.map((entry) => {
          const episode = episodesById.get(entry.episodeId)
          if (!episode) return null
          const showEpisodeBreak = lastEpisodeId != null && entry.episodeId !== lastEpisodeId
          lastEpisodeId = entry.episodeId

          return (
            <div
              key={entry.key}
              ref={(node) => {
                if (node) itemRefs.current.set(entry.key, node)
                else itemRefs.current.delete(entry.key)
              }}
              className="min-w-0"
            >
              {showEpisodeBreak ? (
                <div
                  role="separator"
                  aria-hidden
                  className="h-2 shrink-0 bg-[color-mix(in_srgb,var(--ink)_18%,var(--surface))] sm:h-2.5"
                />
              ) : null}
              <ComicReaderPageContent
                angular={angular}
                episode={episode}
                page={entry.page}
                panelAspect={panelAspect}
                scroll
                onSelectPage={
                  onNavigatePage
                    ? () => onNavigatePage(entry.episodeId, entry.page.id)
                    : undefined
                }
                onSelectPanel={
                  onNavigatePage
                    ? (panelId) => onNavigatePage(entry.episodeId, entry.page.id, panelId)
                    : undefined
                }
              />
            </div>
          )
        })}
        <div className="h-6 shrink-0 sm:h-8" aria-hidden />
      </div>
    </div>
  )
}
